import { ref, computed } from 'vue'
import axios from 'axios'

/**
 * Composable para gerenciar campanhas de notificação
 */
export function useNotificationCampaigns() {
  const campanhas = ref([])
  const loading = ref(false)
  const error = ref(null)
  const paginacao = ref({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  })

  // Estado do modal
  const mostrarModal = ref(false)
  const modoEdicao = ref(false)
  const campanhaSelecionada = ref(null)

  // Passo do wizard
  const passoAtual = ref(1)

  // Status possíveis e suas cores
  const statusConfig = {
    rascunho: { label: 'Rascunho', cor: '#6c757d', icon: 'draft' },
    agendado: { label: 'Agendado', cor: '#0d6efd', icon: 'schedule' },
    em_andamento: { label: 'Em andamento', cor: '#ffc107', icon: 'sync' },
    concluido: { label: 'Concluído', cor: '#198754', icon: 'check_circle' },
    erro: { label: 'Erro', cor: '#dc3545', icon: 'error' },
    cancelado: { label: 'Cancelado', cor: '#6c757d', icon: 'cancel' },
  }

  /**
   * Carregar lista de campanhas com paginação
   */
  async function carregarCampanhas(filtros = {}, pagina = 1) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      params.append('page', pagina)
      if (filtros.canal) params.append('canal', filtros.canal)
      if (filtros.status) params.append('status', filtros.status)
      if (filtros.per_page) params.append('per_page', filtros.per_page)

      const response = await axios.get(`/api/notificacoes/campanhas?${params}`)
      campanhas.value = response.data.data
      paginacao.value = {
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
      }
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao carregar campanhas'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Carregar campanha específica com detalhes
   */
  async function carregarCampanha(id) {
    loading.value = true
    try {
      const response = await axios.get(`/api/notificacoes/campanhas/${id}`)
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Criar nova campanha
   */
  async function criarCampanha(dados) {
    loading.value = true
    try {
      const response = await axios.post('/api/notificacoes/campanhas', dados)
      campanhas.value.unshift(response.data.campanha)
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Atualizar campanha existente
   */
  async function atualizarCampanha(id, dados) {
    loading.value = true
    try {
      const response = await axios.put(`/api/notificacoes/campanhas/${id}`, dados)
      const index = campanhas.value.findIndex(c => c.id === id)
      if (index !== -1) {
        campanhas.value[index] = response.data.campanha
      }
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Excluir campanha
   */
  async function excluirCampanha(id) {
    loading.value = true
    try {
      await axios.delete(`/api/notificacoes/campanhas/${id}`)
      campanhas.value = campanhas.value.filter(c => c.id !== id)
      return true
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Enviar campanha
   */
  async function enviarCampanha(id) {
    loading.value = true
    try {
      const response = await axios.post(`/api/notificacoes/campanhas/${id}/enviar`)
      // Atualizar campanha na lista
      const index = campanhas.value.findIndex(c => c.id === id)
      if (index !== -1) {
        campanhas.value[index] = response.data.campanha
      }
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Cancelar campanha
   */
  async function cancelarCampanha(id) {
    loading.value = true
    try {
      const response = await axios.post(`/api/notificacoes/campanhas/${id}/cancelar`)
      const index = campanhas.value.findIndex(c => c.id === id)
      if (index !== -1) {
        campanhas.value[index] = response.data.campanha
      }
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Carregar destinatários da campanha
   */
  async function carregarDestinatarios(id, pagina = 1) {
    try {
      const response = await axios.get(`/api/notificacoes/campanhas/${id}/destinatarios?page=${pagina}`)
      return response.data
    } catch (err) {
      throw err
    }
  }

  // Funções do modal
  function abrirModalCriar() {
    campanhaSelecionada.value = {
      nome: '',
      template_id: null,
      canal: 'email',
      filtros: {
        uf: [],
        sistema_inscrito: [],
        sexo: [],
        idade_min: null,
        idade_max: null,
      },
      agendado_para: null,
    }
    modoEdicao.value = false
    passoAtual.value = 1
    mostrarModal.value = true
  }

  function abrirModalEditar(campanha) {
    campanhaSelecionada.value = { ...campanha }
    modoEdicao.value = true
    passoAtual.value = 1
    mostrarModal.value = true
  }

  function fecharModal() {
    mostrarModal.value = false
    campanhaSelecionada.value = null
    modoEdicao.value = false
    passoAtual.value = 1
  }

  function avancarPasso() {
    if (passoAtual.value < 3) {
      passoAtual.value++
    }
  }

  function voltarPasso() {
    if (passoAtual.value > 1) {
      passoAtual.value--
    }
  }

  // Computed
  const campanhasAtivas = computed(() =>
    campanhas.value.filter(c => !['concluido', 'cancelado', 'erro'].includes(c.status))
  )

  const campanhasPorStatus = computed(() => {
    return (status) => campanhas.value.filter(c => c.status === status)
  })

  return {
    // State
    campanhas,
    loading,
    error,
    paginacao,
    mostrarModal,
    modoEdicao,
    campanhaSelecionada,
    passoAtual,
    statusConfig,

    // Computed
    campanhasAtivas,
    campanhasPorStatus,

    // Actions
    carregarCampanhas,
    carregarCampanha,
    criarCampanha,
    atualizarCampanha,
    excluirCampanha,
    enviarCampanha,
    cancelarCampanha,
    carregarDestinatarios,

    // Modal
    abrirModalCriar,
    abrirModalEditar,
    fecharModal,
    avancarPasso,
    voltarPasso,
  }
}
